import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import NotificationBell from "./NotificationBell";
import { primaryColor } from "../styles/color";
import { normalizeProposalWorkspacePath } from "../../../utils/proposalWorkspaceRoutes";
import {
  getNotificationsService,
  markNotificationReadService,
} from "../../../services/notification-service/notification-service";
import { mergeFreelancerBellItems } from "../../../utils/freelancerNotifications";

const NOTIFICATION_POLL_MS = 30_000;

/**
 * Bell + merged freelancer notifications (same behaviour as FreelancerHeader).
 * Optional `fetchFlag` lets parents trigger a revision poll refresh when related data changes.
 */
const FreelancerNotificationBell = ({ fetchFlag, borderColor }) => {
  const navigate = useNavigate();
  const [bellItems, setBellItems] = useState([]);
  const userData = JSON.parse(localStorage.getItem("userData") || "null");
  const userId = userData?.user?.id;

  const refreshBell = useCallback(async () => {
    if (userId == null) {
      setBellItems([]);
      return;
    }
    const res = await getNotificationsService({ unreadOnly: false, limit: 25 });
    const serverRows = res?.status === 200 ? res?.data?.result?.data || [] : [];
    setBellItems(mergeFreelancerBellItems(userId, serverRows));
  }, [userId]);

  useEffect(() => {
    refreshBell();
  }, [refreshBell]);

  useEffect(() => {
    const onCustom = () => refreshBell();
    window.addEventListener("liber-freelancer-notifications", onCustom);
    return () => {
      window.removeEventListener("liber-freelancer-notifications", onCustom);
    };
  }, [userId, refreshBell]);

  useEffect(() => {
    if (userId == null) return undefined;

    let cancelled = false;

    async function pollNotifications() {
      if (cancelled) return;
      await refreshBell();
    }

    pollNotifications();
    const intervalId = window.setInterval(pollNotifications, NOTIFICATION_POLL_MS);
    const onVisible = () => {
      if (document.visibilityState === "visible") pollNotifications();
    };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [userId, fetchFlag, refreshBell]);

  return (
    <NotificationBell
      notifications={bellItems}
      borderColor={borderColor ?? primaryColor}
      onNavigate={(url) =>
        navigate(
          normalizeProposalWorkspacePath(url, userData?.user?.user_type),
        )
      }
              onNotificationClick={async (notification) => {
        if (!notification?.id) return;
        await markNotificationReadService(notification.id);
        refreshBell();
      }}
    />
  );
};

export default FreelancerNotificationBell;
